<script lang="ts">
	/**
	 * `/Customers` — the support entry point, and **the only list of people** (§13).
	 *
	 * There were two: this one and `/Users`, which listed the same people from the
	 * other end — the same names and addresses, one screen showing what they had bought and the
	 * other what they were allowed to do. One customer *is* one user, so an administrator looking
	 * for somebody had to know which of the two facts they wanted before they could pick a screen,
	 * and the answer to "who is this person" was never on one page.
	 *
	 * The merged list is driven from **users**, not from customers: a customer row appears when
	 * somebody starts a checkout, so listing customers hides every account that has signed in and
	 * not bought — including, routinely, the administrators. Those rows show their commerce columns
	 * as dashes, which is the truth about them rather than an absence.
	 *
	 * The role is shown here and **changed on the person's own page**, where the rest of what is
	 * known about them is: a promotion made from a list is made without any of the context that
	 * would justify it.
	 *
	 * The search re-queries the server rather than filtering the page in hand: a client-side filter
	 * over one page of twenty-five silently hides every match on page two, which on a support
	 * screen reads as "that customer does not exist".
	 *
	 * Flagged customers are called out in words, not by colour alone — a flag blocks new checkouts,
	 * so "why can this customer not buy anything" has to be answerable at a glance.
	 */

	import { onMount } from 'svelte';
	import {
		Button,
		Input,
		Table,
		TableBody,
		TableBodyCell,
		TableBodyRow,
		TableHead,
		TableHeadCell
	} from 'flowbite-svelte';
	import {
		EmptyState,
		InfiniteLoadElement,
		PageHeader,
		Panel,
		RoutedLink,
		SelectField,
		TableRowsPlaceholder
	} from '../../components';
	import { listAdminCustomers } from '../../AppNetwork';
	import { Badge } from 'flowbite-svelte';
	import { formatAmount } from '../../stores/storefront';
	import { formatCalendarDate } from '../../Utilities';
	import type { AdminCustomer } from '../../AppTypes';

	const PAGE_SIZE = 25;

	let customers: AdminCustomer[] = [];
	let total: number = 0;
	let loading: boolean = true;
	let loadingMore: boolean = false;
	let loadError: string = '';
	let search: string = '';
	/**
	 * Which roles the list is asking for. `''` is everyone.
	 *
	 * Sent to the server rather than applied to the page in hand, for the same reason the search
	 * is: an administrator sitting past the first twenty-five would be filtered out of a list
	 * answering "who can administer this store", and a partial answer there is worse than no
	 * filter — it looks complete.
	 */
	let roleFilter: '' | 'true' | 'false' = '';

	const ROLE_OPTIONS = [
		{ value: '', name: 'Everyone', description: 'Every account that has signed in to the store.' },
		{ value: 'true', name: 'Administrators', description: 'Accounts that can use this console.' },
		{ value: 'false', name: 'Customers', description: 'Everyone else.' }
	];

	/** The paging sentinel. Typed structurally — only its two re-arm methods are used here. */
	let sentinel: { resetVisibilityTracking: () => void; updateState: () => void } | undefined;

	async function fnLoad(reset: boolean = true): Promise<void> {
		if (!reset && (loading || loadingMore || customers.length >= total)) return;

		if (reset) {
			loading = true;
			customers = [];
		} else {
			loadingMore = true;
		}
		loadError = '';

		try {
			const result = await listAdminCustomers({
				search: search.trim() === '' ? undefined : search.trim(),
				isAdmin: roleFilter === '' ? undefined : roleFilter === 'true',
				limit: PAGE_SIZE,
				offset: reset ? 0 : customers.length
			});
			customers = reset ? result.items : [...customers, ...result.items];
			total = result.total;
		} catch (error) {
			loadError = (error as Error).message;
		} finally {
			loading = false;
			loadingMore = false;
			sentinel?.resetVisibilityTracking();
			sentinel?.updateState();
		}
	}

	onMount(() => void fnLoad());
</script>

<PageHeader title="Customers">
	<svelte:fragment slot="description">
		Everyone with an account — what they have bought, and who can administer the store.
	</svelte:fragment>
</PageHeader>

<div class="controlpanel mb-6">
	<form class="flex items-center gap-2" on:submit|preventDefault={() => void fnLoad()}>
		<span class="card-label">Search</span>
		<Input size="sm" class="w-64" placeholder="Name or email" bind:value={search} />
		<Button size="sm" color="alternative" type="submit">Search</Button>
	</form>
	<div class="flex items-center gap-2">
		<span class="card-label">Role</span>
		<!-- Re-queries from the first page: keeping the offset would land the new filter in the
		     middle of a shorter result and show an empty list with more behind it. -->
		<SelectField
			size="sm"
			class="w-40"
			items={ROLE_OPTIONS}
			bind:value={roleFilter}
			on:change={() => void fnLoad()}
		/>
	</div>
	{#if loadError.length === 0}
		<span class="text-sm text-default">{total} {total === 1 ? 'person' : 'people'}</span>
	{/if}
</div>

<Panel title="Customers" faIcon="users">
	{#if loadError.length > 0}
		<EmptyState variant="error" title="Customers could not be loaded." description={loadError}>
			<Button color="alternative" size="sm" on:click={() => fnLoad()}>Try again</Button>
		</EmptyState>
	{:else if !loading && customers.length === 0}
		<!--
			The empty state names the filter that produced it. "Nobody has signed in yet" under an
			Administrators filter is a false statement about the store, and the reader has no way to
			tell it from a real one.
		-->
		<EmptyState
			title={roleFilter === 'true'
				? 'No administrators.'
				: roleFilter === 'false'
					? 'Everybody here is an administrator.'
					: search.trim() === ''
						? 'Nobody has signed in yet.'
						: 'Nobody matches that search.'}
			description={roleFilter !== '' || search.trim() !== ''
				? 'Try a different search, or widen the role filter.'
				: 'A row appears the first time somebody signs in through AbstractID.'}
		/>
	{:else}
		<Table divClass="relative overflow-x-auto">
			<TableHead>
				<TableHeadCell>Person</TableHeadCell>
				<TableHeadCell>Role</TableHeadCell>
				<TableHeadCell>Joined</TableHeadCell>
				<TableHeadCell>Subscriptions</TableHeadCell>
				<TableHeadCell>Purchases</TableHeadCell>
				<TableHeadCell>Credit</TableHeadCell>
				<TableHeadCell>Status</TableHeadCell>
			</TableHead>
			<TableBody>
				{#if loading}
					<TableRowsPlaceholder rows={5} columns={7} />
				{:else}
					{#each customers as customer (customer.userUUID)}
						<TableBodyRow>
							<TableBodyCell class="dataTable">
								<RoutedLink
									to={`/Customers/${customer.userUUID}`}
									class="hover:text-primary-500"
								>
									{customer.billingName ?? customer.displayName ?? 'Unnamed'}
								</RoutedLink>
								<div class="text-xs text-default">
									{customer.billingEmail ?? customer.email ?? ''}
								</div>
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								<!-- Read-only here: the role is changed on the person's own page, where the
								     rest of what is known about them is. -->
								{#if customer.isAdmin}
									<Badge color="green" class="whitespace-nowrap">Admin</Badge>
								{:else}
									<span class="text-default text-sm">Customer</span>
								{/if}
							</TableBodyCell>
							<TableBodyCell class="dataTable">{formatCalendarDate(customer.createdAt)}</TableBodyCell>
							<!--
								Dashes, not zeroes, for somebody who has never checked out. A column of
								`0` beside `—` for the balance would read as a customer who has bought
								nothing *yet*; the dash says the question does not apply to them.
							-->
							<TableBodyCell class="dataTable">
								{customer.isCustomer ? customer.subscriptionCount : '—'}
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								{customer.isCustomer ? customer.orderCount : '—'}
							</TableBodyCell>
							<TableBodyCell class="dataTable tabular-nums">
								<!-- Negative is credit at the provider, and it is why a "full refund" can
								     come up short: that part never moved through the charge. -->
								{!customer.isCustomer || customer.balance === 0
									? '—'
									: ($formatAmount(customer.balance, customer.currency) ?? String(customer.balance))}
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								{#if customer.isFlagged}
									<span class="text-red-600 dark:text-red-400">Flagged</span>
									<div class="text-xs text-default">{customer.flaggedReason ?? ''}</div>
								{:else if customer.isCustomer}
									<span class="text-default">Normal</span>
								{:else}
									<span class="text-default text-sm">Has not bought</span>
								{/if}
							</TableBodyCell>
						</TableBodyRow>
					{/each}
				{/if}
			</TableBody>
		</Table>

		{#if !loading && customers.length < total}
			<InfiniteLoadElement bind:this={sentinel} class="w-full" on:becomeVisible={() => void fnLoad(false)}>
				{#if loadingMore}
					<EmptyState variant="loading" compact title="Loading more…" />
				{/if}
			</InfiniteLoadElement>
		{/if}
	{/if}
</Panel>
